import { useTrackUserEvent } from "@/utils/analytics";
import { useEffect, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import ArrowSeparator from "../ArrowSeparator";
import ConnectWalletButton from "../ConnectWalletButton";
import Button from "../SecondaryButton";
import StakeInput from "../StakeInput";
import { depositToPool } from "@/services/staking";
import Skeleton from "react-loading-skeleton";
import StakeSuccessfulModal from "../Modals/StakeSuccessfulModal";
import ErrorModal from "../Modals/ErrorModal";
import { NotificationType, showToastNotification } from "@/utils/notification";
import {
  isStakingDeprecated,
  networkMap,
  networkType,
  NetworkType,
  STAKING_PAUSED_MESSAGE,
  STAKING_PAUSED_TAB_MESSAGE,
  STAKING_PAUSED_TAB_TITLE,
  STAKING_PAUSED_TITLE,
} from "@/config";
import InfoCircle from "@/assets/info-circle.svg";

interface IProps {
  sponBalance: number | null;
  isLoading: boolean;
  apyPercent: number;
  isApyLoading: boolean;
  switchTab: (tab: "stake" | "unstake") => void;
}

const Staking = ({
  sponBalance,
  isLoading,
  apyPercent,
  isApyLoading,
  switchTab,
}: IProps) => {
  const trackUserEvent = useTrackUserEvent();
  const account = useActiveAccount();
  const [stakingSponAmount, setStakingSponAmount] = useState("");
  const [stakedAmount, setStakedAmount] = useState("");
  const [stakingStatus, setStakingStatus] = useState<
    "" | "staking" | "success" | "error"
  >("");

  const handleMaxClick = () => {
    setStakingSponAmount(sponBalance?.toString() ?? "");
  };

  const stSponAmount = Number(stakingSponAmount) * 1;
  const errorMessage =
    sponBalance !== null && Number(stakingSponAmount) > sponBalance
      ? "Your Available Balance is " + sponBalance + " SPON"
      : "";

  const stake = async () => {
    // Deposits are disabled while staking is deprecated.
    if (isStakingDeprecated) {
      showToastNotification({
        title: STAKING_PAUSED_TITLE,
        message: STAKING_PAUSED_MESSAGE,
        timestamp: new Date(),
        type: NotificationType.Error,
      });
      return;
    }
    if (account) {
      const { blockExplorerUrls } = networkMap[networkType as NetworkType];
      setStakingStatus("staking");
      try {
        const txnHash = await depositToPool(
          BigInt(Number(stakingSponAmount) * 10 ** 18),
          account.address,
          account
        );
        showToastNotification({
          title: "You've successfully staked!",
          message: "",
          txnLink: `${blockExplorerUrls}/tx/${txnHash}`,
          timestamp: new Date(),
          type: NotificationType.Success,
        });
      } catch (e) {
        console.log("Error while deposit: ", e);
        setStakingStatus("error");
        showToastNotification({
          title: "Your staking request couldn't be processed.",
          message: "Please try again or contact support if the issue persists.",
          timestamp: new Date(),
          type: NotificationType.Error,
        });
        return;
      }
      setStakedAmount(stakingSponAmount);
      setStakingSponAmount("");

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const event = new CustomEvent("refreshBalance");
      document?.dispatchEvent(event);
      const event2 = new CustomEvent("refreshStats");
      document?.dispatchEvent(event2);
      setStakingStatus("success");
    }
  };

  useEffect(() => {
    if (!account?.address) setStakingSponAmount("");
  }, [account]);

  return (
    <div className="h-full flex flex-col justify-between">
      {isStakingDeprecated ? (
        <div className="flex flex-col items-center justify-center text-center gap-2 py-10 font-inter">
          <InfoCircle className="w-9 h-9 text-warning" />
          <h3 className="text-white text-lg leading-6 font-semibold">
            {STAKING_PAUSED_TAB_TITLE}
          </h3>
          <p className="text-subtext text-sm leading-[18px] max-w-[320px]">
            {STAKING_PAUSED_TAB_MESSAGE}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-[18px]">
          <div className="flex flex-col gap-3">
            <StakeInput
              label="You Stake"
              unit="SPON"
              value={stakingSponAmount || ""}
              onChange={(e) => {
                setStakingSponAmount(e);
              }}
              error={Boolean(errorMessage)}
            />
            <div className="flex justify-between items-center">
              {errorMessage && account?.address && (
                <div className="text-error font-medium text-sm flex gap-1">
                  {errorMessage}
                </div>
              )}
              {account?.address && !errorMessage && (
                <div className="text-white font-medium text-sm flex gap-1">
                  <span>Available Balance:</span>
                  {isLoading ? (
                    <Skeleton width="100px" />
                  ) : (
                    <span>{sponBalance} SPON</span>
                  )}
                </div>
              )}
              {account?.address && (
                <span
                  onClick={handleMaxClick}
                  className={`bg-white/20 h-6 px-1.5 text-xs items-center justify-center flex rounded-full ${
                    !sponBalance ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  MAX
                </span>
              )}
            </div>
          </div>
          <ArrowSeparator />
          <div className="flex flex-col gap-3">
            <StakeInput
              readOnly
              label="You Receive"
              unit="stSPON"
              value={Number(stSponAmount.toFixed(6)).toString()}
            />
            <div className="flex justify-between items-center">
              <div className="text-subtext font-medium text-sm flex gap-1">
                Current APY Rate
              </div>
              {isApyLoading ? (
                <Skeleton width="50px" height={20} />
              ) : (
                <span className="text-success text-xl font-bold">
                  {apyPercent}%
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {isStakingDeprecated ? (
        <Button
          onClick={() => {
            trackUserEvent("Stake Tab- Go To Unstake Click");
            switchTab("unstake");
          }}
          className="mt-10"
          variant="light"
        >
          GO TO UNSTAKE
        </Button>
      ) : account?.address ? (
        <Button
          onClick={() => {
            trackUserEvent("Stake Tab- Stake Now Click");
            stake();
          }}
          disabled={
            !stakingSponAmount ||
            stakingStatus === "staking" ||
            !Number(stakingSponAmount) ||
            Boolean(errorMessage)
          }
          loading={stakingStatus === "staking"}
          className="mt-10"
          variant="light"
        >
          STAKE NOW
        </Button>
      ) : (
        <ConnectWalletButton />
      )}
      <StakeSuccessfulModal
        close={() => setStakingStatus("")}
        isOpen={stakingStatus === "success"}
        amount={stakedAmount}
        apyPercent={apyPercent}
      />
      <ErrorModal
        close={() => setStakingStatus("")}
        isOpen={stakingStatus === "error"}
        errorTitle="Oops! Something went wrong."
        errorDescription="Your staking request couldn’t be processed. Please try again or contact support if the issue persists."
        handleTryAgain={() => {
          trackUserEvent("Staking Error Modal- Try Again Click");
          stake();
        }}
      />
    </div>
  );
};

export default Staking;
